import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Button, FormControlLabel, Skeleton, Switch } from '@mui/material';
import PageContainer from '../../components/PageContainer';
import { EmptyState, PageHeader, Panel, StatusChip } from '../../components/ui';
import StoriesTagTable from '../../components/StoriesTagsTable';
import StoriesTagFilters from '../../components/StoriesTagFilters';
import { IStore, IOneStory } from '../../store/types';
import { Stories, StoryTags } from '../../store/slices';

const DEFAULT_PAGING_STATE = {
    perPage: 25,
    page: 1,
    total: 0,
};

/** Stories with one column per tag category; a click on a row's video or tags opens the tag assignment popup. */
const StoriesTagsPage = () => {
    const { stories, status, queryParams, storyTags } = useSelector<IStore, { stories: IOneStory[]; status: string; queryParams: any; storyTags: any }>((state) => ({
        stories: Object.values(state.stories.data) as any,
        status: state.stories.status,
        queryParams: state.stories.queryParams,
        storyTags: state.storyTags.data,
    }));
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const [visibleFilters, setVisibleFilters] = useState<string[]>([]);
    const [hideTestData, setHideTestData] = useState(true);
    const authorId = searchParams.get('authorId');

    const loadStories = (params: any) => {
        dispatch<any>(Stories.getStories(params));
    };
    const loadTags = (params: any) => {
        dispatch<any>((StoryTags.getStoryTags as any)(params));
    };

    useEffect(() => {
        loadStories({
            ...queryParams,
            page: queryParams?.page || DEFAULT_PAGING_STATE.page,
            perPage: queryParams?.perPage || DEFAULT_PAGING_STATE.perPage,
            authorId,
        });
        // Reload only when the author in the URL changes; filters and paging reload on their own.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authorId]);

    useEffect(() => {
        loadTags({ page: 1, perPage: 200 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (storyTags?.categories?.length > 0 && visibleFilters.length === 0) {
            const firstFive = storyTags.categories.slice(0, 5).map((c: any) => c.name);
            setVisibleFilters(firstFive);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storyTags.categories]);

    const paging = { ...DEFAULT_PAGING_STATE, ...queryParams };
    const loading = status === 'loading';
    const visible = hideTestData ? stories.filter((story: any) => story.statusId !== 5) : stories;

    return (
        <PageContainer constrained>
            <PageHeader
                eyebrow="Customer Stories"
                title="Stories tags"
                badges={authorId ? <StatusChip tone="brand">Author #{authorId}</StatusChip> : undefined}
                meta={paging.total > 0 ? <span>{Number(paging.total).toLocaleString('en-US')} stories</span> : undefined}
                actions={(
                    <FormControlLabel
                        control={<Switch checked={hideTestData} onChange={(event) => setHideTestData(event.target.checked)} />}
                        label="Hide test data"
                    />
                )}
            />
            <Panel padding="none">
                <StoriesTagFilters
                    queryParams={paging}
                    loadStories={loadStories}
                    loading={loading}
                    visibleFilters={visibleFilters}
                    setVisibleFilters={setVisibleFilters}
                    storyTags={storyTags}
                />
                {loading && stories.length === 0 && (
                    <div style={{ padding: 16, display: 'grid', gap: 10 }} role="status" aria-label="Loading stories">
                        {[0, 1, 2, 3, 4, 5].map((row) => <Skeleton key={row} animation="wave" height={56} />)}
                    </div>
                )}
                {stories.length > 0 && (
                    <div style={{ opacity: loading ? 0.5 : 1, transition: 'opacity .2s', pointerEvents: loading ? 'none' : 'auto' }} aria-busy={loading}>
                        <StoriesTagTable stories={visible} paging={paging} pagingUpdate={loadStories} visibleFilters={visibleFilters} storyTags={storyTags} />
                    </div>
                )}
                {!loading && stories.length === 0 && (
                    <EmptyState
                        title={status === 'rejected' ? 'Stories could not be loaded' : 'No stories match these filters'}
                        hint={status === 'rejected' ? 'The story service did not answer. Try again in a moment.' : 'Change the filters above and press Apply, or clear them.'}
                        action={status === 'rejected' ? <Button variant="outlined" size="small" onClick={() => loadStories(paging)}>Retry</Button> : undefined}
                    />
                )}
                {!loading && stories.length > 0 && visible.length === 0 && (
                    <EmptyState title="Only test data on this page" hint="Turn off “Hide test data” or go to the next page." />
                )}
            </Panel>
        </PageContainer>
    );
};

export default StoriesTagsPage;
